/**
 * context/platformContext.tsx
 * Authenticated-shell state shared across the platform console: the current
 * tenant role (gates the settings nav), platform-staff access (Admin Console),
 * the working copy of workspace data, onboarding dismissal and session lock.
 *
 * Routing is NOT here — that lives in src/navigation/ (React Navigation).
 * Data is seeded from data/platformSeed and refreshed from services as screens
 * load; mutations update this in-memory copy so the UI stays responsive.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { freshState, PlatformState, Role } from '../data/platformSeed';
import { useSession } from './sessionContext';
import { consoleIdentity } from './consoleIdentity';

interface PlatformContextValue {
  role: Role;
  setRole: (r: Role) => void;
  /**
   * True for SOFTKNACK's own people — us, not a tenant's employees. Gates the
   * Admin Console. Mirrored from the session (GET /v1/me); the setter stays for
   * the "view as" override, but the real value now arrives on its own.
   */
  isPlatformAdmin: boolean;
  setPlatformAdmin: (v: boolean) => void;

  state: PlatformState;
  setState: React.Dispatch<React.SetStateAction<PlatformState>>;

  onboardingDismissed: boolean;
  setOnboardingDismissed: (v: boolean) => void;

  sessionLocked: boolean;
  lockSession: () => void;
  unlockSession: () => void;
}

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState<Role>('ADMIN');
  const [isPlatformAdmin, setPlatformAdmin] = useState(false);
  // The real value comes from the session. Without this the flag stayed false
  // forever and every Admin Console screen rendered PermissionDenied.
  const { isPlatformAdmin: sessionIsSoftknackStaff, fullName, user, org } = useSession();
  useEffect(() => { setPlatformAdmin(sessionIsSoftknackStaff); }, [sessionIsSoftknackStaff]);
  // The signed-in user and workspace come from the session, never the demo
  // seed — a customer must not see "Anjali Verma" / "Acme Retail" or the
  // seed's address and GSTIN (see consoleIdentity).
  const [state, setState] = useState<PlatformState>(() => {
    const s = freshState();
    return {
      ...s,
      currentUser: { name: '', email: '' },
      org: { ...s.org, name: '', address: '', gstin: '' },
    };
  });
  useEffect(() => {
    const id = consoleIdentity({ fullName, user, org });
    setState((s) => ({
      ...s,
      currentUser: { name: id.userName, email: id.userEmail },
      org: { ...s.org, name: id.orgName },
    }));
  }, [fullName, user, org]);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [sessionLocked, setSessionLocked] = useState(false);

  const lockSession = useCallback(() => setSessionLocked(true), []);
  const unlockSession = useCallback(() => setSessionLocked(false), []);

  const value = useMemo<PlatformContextValue>(
    () => ({
      role, setRole,
      isPlatformAdmin, setPlatformAdmin,
      state, setState,
      onboardingDismissed, setOnboardingDismissed,
      sessionLocked, lockSession, unlockSession,
    }),
    [role, isPlatformAdmin, state, onboardingDismissed, sessionLocked, lockSession, unlockSession],
  );

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within PlatformProvider');
  return ctx;
}
